import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { T_ApiResponse } from "../../../globalTypes/GlobalTypes";
import { HlLobCode } from "../../../utils/constants";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import {
  useLazyFetchDataServiceDetailsQuery,
  useLazyFetchLanDetailsQuery,
} from "../Homeloan_Api";

import { setHomeLoanInfo, setSummaryData } from "../Homeloan_Slice";

import {
  LoanDetails,
  T_HlDataServiceApiRequest,
  T_HlFetchLanApiRequest,
  T_SummaryData,
} from "../Homeloan_Types";
import { isEmpty, uniq } from "lodash";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { AxiosError } from "axios";
import {
  HL_HandlingReducerAction,
  HL_ReduxInitialState,
} from "./HL_ReduxActionHandler";
import { T_LocalHLBaseData } from "./HL_BaseData_Types";
import { extractUniqueLoanAccountNumbers } from "../../../utils/helper";

export const useHLBaseDataLoader = (
  lanNumber: string = "",
  isHookCalled: boolean = true
): T_LocalHLBaseData => {
  const [localState, updateLocalState] = useReducer(
    HL_HandlingReducerAction,
    HL_ReduxInitialState
  );
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const allSummaryData: T_SummaryData[] = useAppSelector(
    (state) => state.HomeLoanSlice.summaryData
  );
  const loanInfo = useAppSelector((state) => state.HomeLoanSlice.loanInfo);
  const customerAndLobDetails = userDetails?.customerAndLobDetails;
  const [fetchLanDetails, { error: isErrorInLanDetails }] =
    useLazyFetchLanDetailsQuery();
  const [fetchDataServiceDetails, { error: isErrorInDataServiceDetails }] =
    useLazyFetchDataServiceDetailsQuery();
  const allHomeLoanPans = useMemo(() => {
    return !isEmpty(lanNumber)
      ? [lanNumber] // Incase of lan received from props, use same
      : customerAndLobDetails
          // ?.filter((item) => item.LOB === HlLobCode)
          ?.filter(
            (item) =>
              item.LOB === HlLobCode &&
              item.PRIMARY_SECONDARY_USER === "primary"
          )
          .filter((ln) => !isEmpty(ln?.PAN))
          .map((val) => val.PAN) || [];
  }, [lanNumber, customerAndLobDetails]);

  const checkAndinitApiFlow = useCallback(() => {
    if (isEmpty(allHomeLoanPans)) {
      updateLocalState({
        type: "error",
        payload: Error("no pan number found for Home loan"),
      });
    } else if (
      (isEmpty(allSummaryData) || isEmpty(loanInfo.rows)) &&
      !localState.error
    ) {
      updateLocalState({ type: "fetching", payload: "" });
      checkAndInitDetails();
    } else {
      updateLocalState({ type: "success", payload: "" });
    }
  }, [allHomeLoanPans, allSummaryData, loanInfo, localState.error]);

  const api_fetchLanDetailsFromPan = async (panArray: string[]) => {
    let promises = uniq([...panArray]).map((item: string) => {
      const apiRequest: T_HlFetchLanApiRequest = {
        pan: item,
      };
      return fetchLanDetails(apiRequest, true);
    });

    return Promise.all(promises).then((results) => {
      return extractUniqueLoanAccountNumbers(results);
    });
  };

  const checkAndInitDetails = useCallback(async () => {
    const allLanNumbers = isEmpty(lanNumber)
      ? await api_fetchLanDetailsFromPan(allHomeLoanPans)
      : allHomeLoanPans;
    if (allLanNumbers.length <= 0) {
      updateLocalState({ type: "error", payload: Error("Empty List") });
      return;
    }

    let promises = [...allLanNumbers].map((item: string) => {
      const apiRequest: T_HlDataServiceApiRequest = {
        CCC: "",
        LAN: [
          {
            ID: item,
          },
        ],
      };
      return fetchDataServiceDetails(apiRequest, true);
    });
    let results = await Promise.all(promises);
    if (results.some((r) => r.data?.code !== API_STATUS_CODE.SUCCESS)) {
      updateLocalState({ type: "error", payload: Error("Api error") });
    } else {
      let transformedResult = results
        .flatMap((item: any) => item.data?.payload?.data?.Summary_Data || [])
        .filter((val: any) => !isEmpty(val));
      if (isEmpty(transformedResult)) {
        updateLocalState({
          type: "error",
          payload: Error("Empty Summary Data"),
        });
      } else {
        dispatch(setSummaryData(transformedResult));
        /** Process rows if there are transformed results */
        const rows =
          transformedResult.length > 0
            ? transformedResult
                .flatMap((row: T_SummaryData) => row.loan_info?.rows || [])
                .filter((val: LoanDetails | undefined) => !isEmpty(val))
            : [];
        if (isEmpty(rows)) {
          updateLocalState({
            type: "error",
            payload: Error("Empty List for Loan Info"),
          });
        } else {
          dispatch(
            setHomeLoanInfo({
              totalRows: `${rows.length}`,
              moreRows: "false",
              rows,
            })
          );
        }
      }
    }
  }, [allHomeLoanPans]);

  useEffect(() => {
    if (isHookCalled) {
      checkAndinitApiFlow();
    }
  }, [allHomeLoanPans, allSummaryData, loanInfo, isHookCalled]);

  useEffect(() => {
    if (isErrorInLanDetails || isErrorInDataServiceDetails) {
      updateLocalState({
        type: "error",
        payload: (isErrorInLanDetails ||
          isErrorInDataServiceDetails ||
          Error("api failed")) as Error,
      });
    }
  }, [isErrorInLanDetails, isErrorInDataServiceDetails]);

  return localState;
};
